import { useCallback, useEffect, useMemo, useState } from "react";
import { Button, DeviceStage, FormSection, HelpList, Icon, More, Pill, type IconName } from "@omnabase/ui";
import type { PushBrowser, PushListing } from "@omnabase/shared";
import { api } from "../../lib/api";
import { TEST_WORDS, cardState, devicesList, stateWords, type CardState } from "../../lib/pushCard";
import { forgetNotifyRowDismissal } from "../../lib/notifyRow";
import { currentSubscription, listBrowsers, permissionNow, pushSupported, rememberedRow, removeBrowser, subscriptionBody, testBrowser, thisDevice, turnOff, turnOn, type ThisDevice } from "../../lib/push";
import { STAGE_SAMPLE, deviceIntro, deviceLines, deviceWords, isAppleTouch, nothingShows, stageKind, stageSender } from "../../lib/device";
import { HELP_ARTICLE } from "../../lib/pushHint";
import { useSession } from "../../shell/store";

/** Settings › Notifications' Device tab, its top (the device notifications round, 2026-11-01; brief docs/briefs/device-notifications-round.md
 *  rules 2 and 3; before it the "This browser" card of browser notifications round one, 2026-10-18): the panel "Notifications on this <device>"
 *  with the drawing of what turning on turns on, the state in the device's words, the button and the next step; then "Your devices", this one
 *  first. An iPhone or iPad in a browser tab gets the two Home Screen steps instead of a button. The hook also tells the page whether the
 *  Device tab shows (every state but "hidden", no key pair on the server). */
export type DevicePush = { state: CardState; device: ThisDevice; ownRow: PushBrowser | null; rows: PushBrowser[]; busy: boolean; note: string | null; on: () => void; off: () => void; remove: (id: string) => void; /** the forum sync round: "Send a test" */ test: () => void };

export function useDevicePush(slug: string): DevicePush {
  const device = useMemo(thisDevice, []); // read once: the device does not change while the page is open
  const [listing, setListing] = useState<PushListing | undefined>(undefined);
  const [subscribed, setSubscribed] = useState(false);
  const [permission, setPermission] = useState<NotificationPermission | null>(null);
  const [ownId, setOwnId] = useState<string | null>(() => rememberedRow(slug));
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  const refresh = useCallback(async () => {
    const [l, sub, perm] = await Promise.all([listBrowsers(slug).catch(() => ({ publicKey: null, subscriptions: [] })), currentSubscription().catch(() => null), permissionNow()]);
    const own = rememberedRow(slug);
    setPermission(perm); setListing(l); setSubscribed(!!sub); setOwnId(own);
    // a browser may renew its subscription (Firefox): while this workspace has it on, the current keys are stored again (the review's S14)
    if (sub && own && l.subscriptions.some((s) => s.id === own)) void api.put(`/api/v1/w/${encodeURIComponent(slug)}/me/push-subscriptions`, subscriptionBody(sub)).catch(() => undefined);
  }, [slug]);
  useEffect(() => { void refresh(); }, [refresh]);
  const rows = listing?.subscriptions ?? [];
  const ownRow = rows.find((r) => r.id === ownId) ?? null;
  const state = cardState({ publicKey: listing === undefined ? undefined : listing.publicKey, supported: pushSupported(), permission, subscribed, ownRow, device });
  const act = (work: () => Promise<void>) => { setBusy(true); setNote(null); void work().catch(() => setNote("That did not work. Try again in a moment.")).finally(() => setBusy(false)); };
  const on = () => act(async () => {
    const r = await turnOn(slug, listing!.publicKey!);
    if (!r.ok) { setNote(r.reason === "dismissed" ? "You dismissed the question. Turn on again to answer it." : null); await refresh(); return; }
    setOwnId(r.row.id); setSubscribed(true); setListing((l) => (l ? { ...l, subscriptions: [...l.subscriptions.filter((s) => s.id !== r.row.id), r.row] } : l));
    await sendTest(r.row.id); // the forum sync round (finding 14): one test pop-up right after Turn on; storing a device never sends by itself
  });
  /** one test pop-up to this device; the card says what the push service answered, or the server's "wait a minute" */
  const sendTest = async (id: string) => {
    try { const r = await testBrowser(slug, id); setNote(TEST_WORDS[r.outcome]); if (r.outcome === "gone") { setOwnId(null); setSubscribed(false); await refresh(); } }
    catch (e) { setNote((e as Error).message || "That did not work. Try again in a moment."); }
  };
  const test = () => { if (ownRow) act(() => sendTest(ownRow.id)); };
  const off = () => act(async () => { if (ownRow) await turnOff(slug, ownRow.id); forgetNotifyRowDismissal(slug); setOwnId(null); setSubscribed(false); await refresh(); }); // the home-and-customers round: a new decision, so Home's row may ask again
  const remove = (id: string) => act(async () => { await removeBrowser(slug, id); if (id === ownId) forgetNotifyRowDismissal(slug); setListing((l) => (l ? { ...l, subscriptions: l.subscriptions.filter((s) => s.id !== id) } : l)); });
  return { state, device, ownRow, rows, busy, note, on, off, remove, test };
}

const UNBLOCK = ["Chrome and Edge: the icon left of the address, then Notifications.", "Firefox: the icon left of the address, then the blocked permission.", "Safari: Settings › Websites › Notifications."];

/** the panel: the drawing, then the state with its button and the next step */
export function DevicePanel({ push }: { push: DevicePush }) {
  const { workspace } = useSession();
  const { state, device } = push;
  if (state === "hidden" || state === "loading") return null;
  const touchIcon = isAppleTouch(device.kind) && workspace.logo ? <img src={workspace.logo} alt="" /> : undefined; // the Home Screen app's own icon
  return (
    <FormSection title={`Notifications on ${deviceWords(device.kind)}`} intro={deviceIntro(device.kind, device.browser)}>
      <div className="ob-device-panel">
        <DeviceStage kind={stageKind(device.kind, state === "homeScreen")} title={STAGE_SAMPLE.title} body={STAGE_SAMPLE.body} sender={stageSender(device.kind, device.browser)} icon={touchIcon} />
        <div className="ob-device-panel__words">
          <StateRow push={push} />
          <NextStep state={state} device={device} />
          {push.note && <p className="ob-form-row__help ob-push-card__note">{push.note}</p>}
          {state === "blocked" && <More label="How to unblock"><HelpList items={UNBLOCK} /></More>}
          {state === "on" && <More label="Nothing shows?"><HelpList items={nothingShows(device.kind, device.browser)} /></More>}
        </div>
      </div>
    </FormSection>
  );
}

/** the kit's row markup, written out so the state reads as its own words beside the button (a space between them in the text) */
function StateRow({ push }: { push: DevicePush }) {
  const { state, device } = push;
  return (
    <div className="ob-form-row ob-push-card__state">
      <div className="ob-form-row__text"><div className="ob-form-row__label">{stateWords(state, push.ownRow, device)}</div>{" "}{state === "blocked" && <div className="ob-form-row__help">Allow them again in the browser: <a className="ob-link" href={HELP_ARTICLE} target="_blank" rel="noopener noreferrer">how, browser by browser</a>.</div>}</div>{" "}
      <div className="ob-form-row__control">
        {state === "off" && <Button variant="primary" busy={push.busy} onClick={push.on}>Turn on</Button>}
        {state === "on" && <span className="ob-push-card__buttons"><Button disabled={push.busy} onClick={push.test}>Send a test</Button>{" "}<Button busy={push.busy} onClick={push.off}>Turn off</Button></span>}
      </div>
    </div>
  );
}

/** the line(s) under the state; the Home Screen state's two steps as a numbered list */
function NextStep({ state, device }: { state: CardState; device: ThisDevice }) {
  const lines = deviceLines(device.kind, state, { standalone: device.standalone, browser: device.browser });
  if (!lines.length) return null;
  if (state === "homeScreen") return <ol className="ob-form-row__help ob-device-panel__steps">{lines.map((l) => <li key={l}>{l}</li>)}</ol>;
  return <>{lines.map((l) => <p key={l} className="ob-form-row__help ob-device-panel__line">{l}</p>)}</>;
}

const shortDate = (iso: string) => new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
/** a row's icon by its name: a phone, a tablet, or a computer */
const rowIcon = (name: string): IconName => (/^iPad\b/.test(name) ? "tablet" : /^iPhone\b|Android/.test(name) ? "smartphone" : "device");

/** "Your devices": every device of the person in this workspace, this one first with the pill and without a second button; none, no list */
export function YourDevices({ push }: { push: DevicePush }) {
  if (push.state === "hidden" || push.state === "loading" || push.rows.length === 0) return null;
  const ownId = push.ownRow?.id ?? null;
  return (
    <FormSection title="Your devices">
      <ul className="ob-push-card__list">
        {devicesList(push.rows, ownId).map((b) => (
          <li key={b.id} className="ob-push-card__item">
            <span className="ob-push-card__icon"><Icon name={rowIcon(b.browser)} size="sm" /></span>
            <span className="ob-push-card__name">{b.browser}</span>{" "}
            {b.id === ownId && <Pill>This device</Pill>}{" "}
            <span className="ob-push-card__meta">{b.disabledReason ? `Switched off: ${b.disabledReason}` : `On since ${shortDate(b.createdAt)}`}</span>
            {b.id !== ownId && <Button size="sm" aria-label={`Remove ${b.browser}`} disabled={push.busy} onClick={() => push.remove(b.id)}>Remove</Button>}
          </li>
        ))}
      </ul>
    </FormSection>
  );
}
